<template>
    <h2
        ref="headerelement"
        class="pt-2 ps-2 header"
        style="font-size: calc(var(--iframe-text-size, 100%) * 2)"
    >
        {{ $tm(data?.Title ?? '')
        }}<small
            class="display-4 ms-4"
            style="font-size: calc(var(--iframe-text-size, 100%) * 0.75)"
            >{{ $tm(data?.Subtitle ?? '', '', false) }}</small
        >
    </h2>
    <table
        ref="tableelement"
        class="production-table"
        style="
            --padding: 0.25em;
            --row-margin: 1em;
            width: 100%;
            max-width: 100%;
            color: var(--iframe-text-color);
            font-size: var(--iframe-text-size, 100%);
        "
    >
        <thead>
            <tr style="font-weight: bold">
                <template
                    v-for="column in table.Columns"
                    :key="column"
                >
                    <th
                        :class="[`col-${column.toString().toLowerCase()}`]"
                        :style="getColumnHeaderStyle(column)"
                    >
                        {{ $tm(`productiontable.col${column.toString().toLowerCase()}`) }}
                    </th>
                </template>
            </tr>
        </thead>
        <tbody>
            <tr
                v-for="row in _rows"
                :key="row.Index"
                :style="row.GetRowStyle(table, setting)"
            >
                <td
                    v-for="column in table.Columns"
                    :key="column"
                    style="padding: var(--padding)"
                    :class="[`col-${column.toString().toLowerCase()}`]"
                >
                    <div
                        :style="row.GetStyle(column, table.Columns)"
                        class="overflow-auto"
                        :class="{ 'fade-in': fadeInRows.has(row.Index) }"
                    >
                        {{ row.GetValue(column) }}
                    </div>
                </td>
            </tr>
        </tbody>
    </table>
</template>
<style>
@keyframes productionFadeoutBackground {
    0% {
        background-color: v-bind(RowFadeoutColor);
    }
    20% {
        background-color: v-bind(RowFadeoutColor);
    }
    100% {
        background-color: 'transparent';
    }
}
</style>
<script lang="ts" setup>
import { onMounted, watch, onUnmounted, ref, toRaw, computed } from 'vue';
import { ProductionTableRow } from './ProductionTableRow';
import {
    ProductionDeviceSetting,
    ProductionDisplayData,
    ProductionDisplayDataItem,
    ProductionTableDef,
    type ProductionEvent,
} from '@/objects/generated';
import type { ProductionColumnType } from '@/objects/generated/enums/ProductionColumnType';
import { ProductionDao } from '@/dao';
import { useEventStore } from '@/stores';
import signalrSubscribe from '@/objects/generated/signalr/signalrSubscribe';
import { $tm } from '@/locize';

const props = defineProps<{
    table: ProductionTableDef;

    /**
     * The setting to display.
     */
    setting: ProductionDeviceSetting;

    preview?: boolean;
}>();

const { eventID } = useEventStore();

const data = ref<ProductionDisplayData | null>(null);
const _rows = ref<ProductionTableRow[]>([]);

const headerelement = ref<HTMLHeadingElement | null>(null);
const tableelement = ref<HTMLTableElement | null>(null);

const RowFadeoutColor = ref<string>(props.setting.RowFadeoutColor ?? '#000000');

// We need to find the available space for the number of rows we have

// TODO: We need to start the poller here.
let dataPollerId: number | null = null;

let filterKeys: ProductionTableDef[] = [];

let pollerIndex = 0;

// We use this one in case there is remaining data in the poller so we need to call the same call again anoter startAtIndex
let pollerStartAt = 0;

/**
 * This is the event that we are currently displaying with the neccessary data
 */
const event = ref<ProductionEvent | null>(null);

/**
 * this will poll the background for new data. It also have to handle the loop of the keys,
 * so we make sure we loop through all the lists correctly and present all the data.
 *
 */
const startPoller = () => {
    if (filterKeys.length === 0) {
        return;
    }
    pollerFunction();
};

let fadeInRows = new Map<string, Date>();

const computedStationRoleIds = ref<number[]>([]);

/**
 * Update the computed station role ids based on the table station roles
 */
const updateStationRoleFilter = () => {
    if (!props.table.StationRoles || props.table.StationRoles.length == 0) {
        computedStationRoleIds.value = [];
        return;
    }
    let ids: number[] = [];
    for (const stationRole of props.table.StationRoles) {
        let raceStationroleIDs = stationRole.split('-');
        if (raceStationroleIDs.length == 2) {
            ids.push(parseInt(raceStationroleIDs[1]));
        }
    }
    computedStationRoleIds.value = ids;
};

updateStationRoleFilter();

/**
 * This is the handler for the data that we receive from the signalR subscription
 * i.e. Scroll data
 * @param data
 */
const handleReceivedData = (items: ProductionDisplayDataItem[]) => {
    let dataset = toRaw(_rows.value);

    let headerdata = ProductionDisplayData.FromJsValues({});

    headerdata.Title = props.table.OverrideTitle ?? $tm('productiontable.scrolltitle');
    headerdata.Subtitle = props.table.OverrideSubtitle ?? '';

    // remove all the rows that dont match the stationRoleFilter
    if (computedStationRoleIds.value.length > 0) {
        items = items?.filter((row) => {
            return computedStationRoleIds.value.includes(row.StationRoleId);
        });
    }

    // Filter the data to remove the points we dont want displayed using the filterkeys
    items = items.filter((item) => {
        return (
            filterKeys.find((key) => key.RaceId === item.RaceId && (key.CategoryId == 0 || key.CategoryId === item.CategoryId)) !==
            undefined
        );
    });

    let newrows = convertDataItems(items)?.reverse();

    // Remove all the newrows with the same index to avoid duplicates
    newrows = newrows?.filter((row) => {
        return dataset.find((r) => r.Index === row.Index) === undefined;
    });

    // Add the rows to the beginning of the list
    newrows?.forEach((row) => {
        // If the index allready exists, we update it, else we add it
        let index = dataset.findIndex((r) => r.Index === row.Index);

        if (index >= 0) {
            dataset[index] = row;
        } else {
            // We add the fadeinrow to the map
            fadeInRows.set(row.Index, new Date());
            dataset.unshift(row);
        }
    });

    // Remove all the fadeinitems with a value older than 2 seconds
    fadeInRows = new Map(
        [...fadeInRows].filter(([key, value]) => {
            return new Date().getTime() - value.getTime() < 2000;
        }),
    );

    const numberOfLines = 55; // props.setting.NumberOfLines ?? props.setting.NumberOfLinesCalculated ?? 25;

    // Make sure we only have the max number of rows
    if (dataset.length > numberOfLines) {
        dataset = dataset.slice(0, numberOfLines);
    }

    _rows.value = dataset;
    data.value = headerdata;
};

/**
 * Helper function to convert the dataitems to the correct format for the table
 * @param dataitems
 */
const convertDataItems = (dataitems: ProductionDisplayDataItem[]) => {
    let rows: ProductionTableRow[] = [];
    dataitems?.forEach((item) => {
        let row = new ProductionTableRow();
        row.Index = `${item.RaceContestantId}-${item.Round}-${item.StationRoleId}`;
        row.Bib = item.Bib?.toString();
        row.Name = item.Name ?? '';
        row.TeamClub = item.ClubTeam ?? '';
        row.Diff = (item.TimeStatus != 'Tid' ? '' : item.Behind) ?? '';
        row.Time = (item.TimeStatus != 'Tid' ? item.TimeStatus : item.ElapsedTime) ?? '';
        row.Meters = (item.TimeStatus != 'Tid' ? '' : item.Additionals) ?? '';
        row.Points = (item.TimeStatus != 'Tid' ? '' : item.Additionals) ?? '';
        row.Lap = item.Round?.toString();
        row.Km = (item.TimeStatus != 'Tid' ? '' : item.Additionals) ?? '';
        row.Rank = (item.TimeStatus != 'Tid' ? '' : item.Rank?.toString()) ?? '';
        row.StationRoleName = item.StationRoleName ?? '';
        row.FromSignalR = item.FromSignalR ?? false;
        row.CategoryName = categoryMap.value.get(item.CategoryId ?? 0) ?? '';

        if (row.Rank == '0') {
            row.Rank = '';
        }

        row.StartingTime = item.StartingTime ?? '';

        rows.push(row);
    });

    return rows;
};

// This is a map of the keys that we are waiting for new data on
// We do this so we dont get an infinite loop of polling the same key
let waitNewPoller = new Map<number, Date>();

/**
 * The poller function that will poll the background for new data. This is resultlists and startinglists
 */
const pollerFunction = async () => {};

/**
 * Get the style for the column header
 * @param column The column to get the style for
 */
const getColumnHeaderStyle = (column: ProductionColumnType) => {
    let styles: string[] = [];

    styles.push('padding: var(--padding)');
    styles.push('border-bottom: thin solid var(--iframe-text-color, white)');

    if (!props.table.Columns.includes(column)) {
        styles.push('display: none');
    }

    switch (column) {
        case 'BIB':
        case 'RANK':
        case 'LAP':
            styles.push('text-align: center');
            styles.push('max-width: 10%');
            break;
        case 'DIFF':
        case 'POINTS':
        case 'METERS':
        case 'KM':
        case 'TIME':
        case 'STARTTIME':
            styles.push('text-align: right');
            break;
        default:
            styles.push('text-align: left');
            break;
    }

    // We set the width on the cells we know
    switch (column) {
        case 'BIB':
        case 'RANK':
            styles.push('width: 5%;');
            break;
        case 'DIFF':
        case 'TIME':
        case 'STARTTIME':
            styles.push('width: 15%;');
            break;
        case 'POINTS':
        case 'METERS':
        case 'KM':
            styles.push('width: 10%;');
            break;
    }

    return styles.join('; ');
};

/**
 * Watch for changes in the table and restart the handler
 */
watch(
    () => props.table,
    () => {
        updateStationRoleFilter();
        stopHandler();
        startHandler();

        RowFadeoutColor.value = props.setting.RowFadeoutColor ?? '#000000';
    },
);

/**
 * Helper map to easy access the Categoryname
 */
const categoryMap = computed(() => {
    let map = new Map<number, string>();

    event.value?.Races.forEach((race) => {
        race.Categories?.forEach((category) => {
            map.set(category.ID, category.Name ?? '');
        });
    });
    return map;
});

watch(
    () => props.setting,
    async (nv, ov) => {
        // Here we need to load whatever data we need
        // We need to reload the event
        event.value = await ProductionDao.GetProductionEvent(props.setting.EventId);

        console.log('prodsettings changed', nv, ov, event.value);
    },
    { deep: true },
);

/**
 * This creates the initial keys for the poller based on the event data
 * This is all keys for races and categories filtered on the definition we send in
 *
 * If we dont send a filter, we will poll all the races and categories in the event
 */
const createFilterKeys = () => {
    filterKeys = [];

    if (event.value === null) {
        return filterKeys;
    }

    event.value.Races.forEach((race) => {
        let categoryIDS = race.Categories?.map((c) => c.ID) ?? [];

        // We need to check the ResultType to see if we should filter the categories or not
        // If it is TOTAL, we only want the total category 0
        // If it is GENDER we use the Category for Male (1) and Female (2)

        if (props.table.DisplayType == 'RESULTLIST') {
            if (props.table.ResultType == 'TOTAL') {
                categoryIDS = [0];
            } else if (props.table.ResultType == 'GENDER') {
                categoryIDS = [1, 2];
            }
        }

        for (const categoryID of categoryIDS) {
            let include = true;
            if (props.table.Categories?.length > 0) {
                include = props.table.Categories.includes(`${race.ID}-${categoryID}`);
            }

            console.log('Checking if we should include category', categoryID, props.table.Categories, include);

            if (include) {
                // We need to add one filterkey for each stationRoleId
                props.table.StationRoles.forEach((raceStationroleIds) => {
                    const ids = raceStationroleIds.split('-');

                    if (ids.length !== 2) {
                        return;
                    }
                    const raceId = parseInt(ids[0]);
                    const stationRoleId = parseInt(ids[1]);

                    // if this does not belong to the current race we skip it
                    if (race.ID !== raceId) {
                        return;
                    }

                    const fk = ProductionTableDef.FromJsValues({
                        EventId: event.value?.ID,
                        RaceId: raceId,
                        Lap: 0,
                        CategoryId: categoryID,
                        StartFrom: 0,
                        ItemCount: 55, // props.setting.NumberOfLines ?? props.setting.NumberOfLinesCalculated ?? 25,
                        Columns: props.table.Columns,
                        StationRoleId: stationRoleId,
                        AvailableColumns: props.table.AvailableColumns,
                        DisplayType: props.table.DisplayType,
                        Categories: props.table.Categories,
                        StationRoles: props.table.StationRoles,
                    });
                    fk.ResultType = props.table.ResultType;
                    fk.DisplayType = props.table.DisplayType;
                    fk.RotateTimeInSeconds = props.table.RotateTimeInSeconds;
                    filterKeys.push(fk);
                });
            }
        }
    });

    console.log('CREATED FILTER KEYS', filterKeys);
};

/**
 * Set up the handler based on the table
 */
const startHandler = async () => {
    // We either set up a poller or a signalR subscription here
    if (props.preview) {
        return;
    }

    createFilterKeys();

    if (props.table.DisplayType === 'SCROLL') {
        const headerdata = ProductionDisplayData.FromJsValues({
            Title: props.table.OverrideTitle ?? $tm('productiontable.scrolltitle'),
            Subtitle: props.table.OverrideSubtitle ?? '',
        });
        data.value = headerdata;

        signalrSubscribe.ProductionDisplayDataItem({ EventID: props.table.EventId }, (data: ProductionDisplayDataItem[]) => {
            handleReceivedData(data);
        });
    } else {
        startPoller();
    }
};

/**
 * Stops the handler and clean up all resources. i.e stop intervals and signalR subscriptions
 */
const stopHandler = () => {
    // We reset the current title and data
    if (data.value) {
        data.value.Title = '';
        data.value.Subtitle = '';
    }
    _rows.value = [];

    if (dataPollerId !== null) {
        window.clearTimeout(dataPollerId);
        dataPollerId = null;
    }
};

/**
 * Start the handler when the component is mounted
 */
onMounted(async () => {
    // We either set up a poller or a signalR subscription here
    if (props.preview) {
        return;
    }

    event.value = await ProductionDao.GetProductionEvent(props.setting.EventId);

    startHandler();
});

/**
 * Clean up all resources when the component is unmounted
 */
onUnmounted(() => {
    stopHandler();
});

/**
 * Only data used for preview
 */
if (props.preview) {
    data.value = ProductionDisplayData.FromJsValues({
        Title: 'Race 1',
        Subtitle: 'Category Elite',
    });

    let row = new ProductionTableRow();
    row.Bib = '747';
    row.Name = 'John McClane';
    row.TeamClub = 'Die Hard cyclists';
    row.Diff = '+01:43.1';
    row.Time = '01:42:12.3';
    row.Meters = '1000';
    row.Points = '100';
    row.Lap = '1';
    row.Km = '36.2';
    row.Rank = '1';
    row.StartingTime = '12:00:00';
    row.StationRoleName = 'Finish';
    row.CategoryName = 'Elite';

    let row2 = new ProductionTableRow();
    row2.Bib = '42';
    row2.Name = 'Arthur Dent';
    row2.TeamClub = 'Hitchhikers';
    row2.Diff = '+01:43.1';
    row2.Time = '01:42:12.3';
    row2.Meters = '1000';
    row2.Points = '100';
    row2.Lap = '1';
    row2.Km = '36.2';
    row2.Rank = '2';
    row2.StartingTime = '12:10:00';
    row2.StationRoleName = 'Finish';
    row2.CategoryName = 'Elite';

    let row3 = new ProductionTableRow();
    row3.Bib = '17';
    row3.Name = 'Ford Prefect';
    row3.TeamClub = 'Hitchhikers';
    row3.Diff = '+01:48.4';
    row3.Time = '01:42:17.4';
    row3.Meters = '1250';
    row3.Points = '95';
    row3.Lap = '1';
    row3.Km = '32.2';
    row3.Rank = '3';
    row3.StartingTime = '12:20:00';
    row3.StationRoleName = 'Finish';
    row3.CategoryName = 'Elite';

    let row4 = new ProductionTableRow();
    row4.Bib = '23';
    row4.Name = 'Zaphod Beeblebrox';
    row4.TeamClub = 'Hitchhikers and Presidents';
    row4.Diff = '+02:48.4';
    row4.Time = '01:53:17.4';
    row4.Meters = '1550';
    row4.Points = '75';
    row4.Lap = '1';
    row4.Km = '29.2';
    row4.Rank = '4';
    row4.StartingTime = '12:30:00';
    row4.StationRoleName = 'Finish';
    row4.CategoryName = 'Elite';

    let row5 = new ProductionTableRow();
    row5.Bib = '1';
    row5.Name = 'Slartibartfast';
    row5.TeamClub = 'Comic book heroes';
    row5.Diff = '+03:48.4';
    row5.Time = '02:03:17.4';
    row5.Meters = '1750';
    row5.Points = '55';
    row5.Lap = '1';
    row5.Km = '26.2';
    row5.Rank = '5';
    row5.StartingTime = '12:40:00';
    row5.StationRoleName = 'Finish';
    row5.CategoryName = 'Amateur';

    let row6 = new ProductionTableRow();
    row6.Bib = '2';
    row6.Name = 'Marvin';
    row6.TeamClub = 'Comic book heroes';
    row6.Diff = '+04:48.4';
    row6.Time = '02:18:17.4';
    row6.Meters = '1950';
    row6.Points = '35';
    row6.Lap = '1';
    row6.Km = '23.1';
    row6.Rank = '6';
    row6.StartingTime = '12:50:00';
    row6.StationRoleName = 'Finish';
    row6.CategoryName = 'Amateur';

    _rows.value = [row, row2, row3, row4, row5, row6];
}
</script>
